import type { WritableAtom } from "jotai";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useMemo } from "react";

import type {
	ShipmentAdditionalDetails,
	ShipmentCarrier,
	ShipmentCustomer,
	ShipmentFreightDetails,
	ShipmentLineItem,
	ShipmentManagementPickupDeliveryDetails,
} from "~/shared/schemas/shipment-management-schema.ts";
import type { StopContact, StopName } from "~/shared/types/stop-types.ts";

import {
	shipmentAccessorialsDraftAtom,
	shipmentCarrierDraftAtom,
	shipmentCustomerDraftAtom,
	shipmentDraftAtom,
	shipmentValidationRequestedAtom,
} from "../state/shipment-edit-atoms.ts";
import type { ShipmentValidationErrors } from "../utils/validate-shipment-draft.ts";
import { validateShipmentDraft } from "../utils/validate-shipment-draft.ts";

const toNumericString = (value: string | null | undefined): number => {
	if (value == null || value === "") {
		return 0;
	}
	const parsed = Number(value);
	return Number.isNaN(parsed) ? 0 : parsed;
};

const updateFreightTotals = (
	freightDetails: ShipmentFreightDetails,
	lineItems: ShipmentLineItem[],
): ShipmentFreightDetails => {
	const totalHandlingUnits = lineItems.reduce(
		(sum, item) => sum + toNumericString(item.pieceCount),
		0,
	);
	const totalWeight = lineItems.reduce(
		(sum, item) => sum + toNumericString(item.totalWeight),
		0,
	);

	return {
		...freightDetails,
		lineItems,
		totalHandlingUnits: String(totalHandlingUnits),
		totalWeight: String(totalWeight),
	};
};

/**
 * Read/write a single pickup or delivery stop on the shipment draft. Returns the
 * current stop (or null when no draft is seeded) alongside patch helpers that
 * merge partial updates back into the shared draft. Updates no-op when no draft
 * is present so components can render safely outside of edit mode.
 */
export const useStopDraft = (name: StopName) => {
	const [draft, setDraft] = useAtom(shipmentDraftAtom);

	const updateStop = useCallback(
		(patch: Partial<ShipmentManagementPickupDeliveryDetails>) =>
			setDraft((prev) =>
				prev ? { ...prev, [name]: { ...prev[name], ...patch } } : prev,
			),
		[name, setDraft],
	);

	const updateContact = useCallback(
		(patch: Partial<StopContact>) =>
			setDraft((prev) =>
				prev
					? {
							...prev,
							[name]: {
								...prev[name],
								contact: { ...prev[name]?.contact, ...patch },
							},
						}
					: prev,
			),
		[name, setDraft],
	);

	return { stop: draft?.[name] ?? null, updateStop, updateContact };
};

/**
 * Read/write the freight line items on the shipment draft. `updateLineItem`
 * merges a partial patch into the item at the given index.
 */
export const useFreightDraft = () => {
	const [draft, setDraft] = useAtom(shipmentDraftAtom);

	const updateLineItem = useCallback(
		(index: number, patch: Partial<ShipmentLineItem>) =>
			setDraft((prev) =>
				prev
					? {
							...prev,
							freightDetails: updateFreightTotals(
								prev.freightDetails,
								prev.freightDetails.lineItems.map((item, i) =>
									i === index ? { ...item, ...patch } : item,
								),
							),
						}
					: prev,
			),
		[setDraft],
	);

	const removeLineItem = useCallback(
		(index: number) =>
			setDraft((prev) =>
				prev
					? {
							...prev,
							freightDetails: updateFreightTotals(
								prev.freightDetails,
								prev.freightDetails.lineItems.filter((_, i) => i !== index),
							),
						}
					: prev,
			),
		[setDraft],
	);

	const addLineItem = useCallback(
		(item: ShipmentLineItem) =>
			setDraft((prev) =>
				prev
					? {
							...prev,
							freightDetails: updateFreightTotals(prev.freightDetails, [
								...prev.freightDetails.lineItems,
								item,
							]),
						}
					: prev,
			),
		[setDraft],
	);

	// Patch top-level freight fields (e.g. the linear-feet overwrite) on the
	// draft, leaving the line items untouched.
	const updateFreightDetails = useCallback(
		(patch: Partial<Omit<ShipmentFreightDetails, "lineItems">>) =>
			setDraft((prev) =>
				prev
					? {
							...prev,
							freightDetails: { ...prev.freightDetails, ...patch },
						}
					: prev,
			),
		[setDraft],
	);

	return {
		lineItems: draft?.freightDetails.lineItems ?? null,
		freightDetails: draft?.freightDetails ?? null,
		updateLineItem,
		removeLineItem,
		addLineItem,
		updateFreightDetails,
	};
};

/**
 * Read/write the reference (additional) details on the shipment draft.
 */
export const useAdditionalDetailsDraft = () => {
	const [draft, setDraft] = useAtom(shipmentDraftAtom);

	const updateAdditionalDetails = useCallback(
		(patch: Partial<ShipmentAdditionalDetails>) =>
			setDraft((prev) =>
				prev
					? {
							...prev,
							additionalDetails: { ...prev.additionalDetails, ...patch },
						}
					: prev,
			),
		[setDraft],
	);

	return {
		additionalDetails: draft?.additionalDetails ?? null,
		updateAdditionalDetails,
	};
};

/**
 * Read/write the editable carrier fields (PRO number, agent PRO) on the carrier
 * draft. Falls back to no-op when no draft is seeded so it's safe to call
 * outside edit mode.
 */
export const useCarrierDraft = () => {
	const [carrier, setCarrier] = useAtom(shipmentCarrierDraftAtom);

	const updateCarrier = useCallback(
		(patch: Partial<ShipmentCarrier>) =>
			setCarrier((prev) => (prev ? { ...prev, ...patch } : prev)),
		[setCarrier],
	);

	return { carrier, updateCarrier };
};

/**
 * Read/write the shipment's customer (customer selection and billing customer)
 * on its own draft slice. Mirrors {@link useCarrierDraft}: writes merge a
 * partial patch into the seeded customer while editing.
 */
export const useCustomerDraft = () => {
	const [customer, setCustomer] = useAtom(shipmentCustomerDraftAtom);

	const updateCustomer = useCallback(
		(patch: Partial<ShipmentCustomer>) =>
			setCustomer((prev) => (prev ? { ...prev, ...patch } : prev)),
		[setCustomer],
	);

	return { customer, updateCustomer };
};

/**
 * Read/write the shipment-level selected accessorial names on their own draft
 * slice. This keeps the freight-card accessorial checkboxes independent from
 * the stop-level accessorial editors.
 */
export const useShipmentAccessorialsDraft = () => {
	const shipmentAccessorialsAtom =
		shipmentAccessorialsDraftAtom as WritableAtom<
			string[] | null,
			[string[] | null],
			void
		>;
	const accessorials = useAtomValue(shipmentAccessorialsAtom);
	const setAccessorials = useSetAtom(shipmentAccessorialsAtom);

	const updateAccessorials = useCallback(
		(nextAccessorials: string[]) => setAccessorials(nextAccessorials),
		[setAccessorials],
	);

	return { accessorials, updateAccessorials };
};

/**
 * Computes the current shipment draft validation errors, but only after the user
 * has attempted to save (so inline messages don't appear before the first save).
 * Returns `null` before a save has been attempted or when no draft is seeded.
 * Errors clear live as the user fills in the required fields.
 */
export const useShipmentValidationErrors =
	(): ShipmentValidationErrors | null => {
		const draft = useAtomValue(shipmentDraftAtom);
		const validationRequested = useAtomValue(shipmentValidationRequestedAtom);

		return useMemo(
			() =>
				validationRequested && draft ? validateShipmentDraft(draft) : null,
			[validationRequested, draft],
		);
	};
